'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, Palette, Gem, Sparkles, Shield, ChevronDown, X, Clock } from 'lucide-react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useRTL } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);

export default function TypesOfVeneersSection() {
    const { t } = useTranslation('dental_veneers');
    const { isRTL } = useRTL();
    
    const veneerTypes = [
    {
        id: 'porcelain',
        icon: Crown,
        title: t('types.veneerTypes.porcelain.title'),
        tagline: t('types.veneerTypes.porcelain.tagline'),
        attributes: [
            t('types.veneerTypes.porcelain.specs.lifespan'),
            t('types.veneerTypes.porcelain.specs.stainResistant'),
            t('types.veneerTypes.porcelain.specs.naturalLook')
        ],
        description: t('types.veneerTypes.porcelain.description'),
        lifespan: t('types.veneerTypes.porcelain.lifespan'),
        idealFor: t('types.veneerTypes.porcelain.idealFor'),
        keyFeatures: t('types.veneerTypes.porcelain.keyFeatures', { returnObjects: true }) as string[]
    },
    {
        id: 'composite',
        icon: Palette,
        title: t('types.veneerTypes.composite.title'),
        tagline: t('types.veneerTypes.composite.tagline'),
        attributes: [
            t('types.veneerTypes.composite.specs.lifespan'),
            t('types.veneerTypes.composite.specs.singleVisit'),
            t('types.veneerTypes.composite.specs.affordable')
        ],
        description: t('types.veneerTypes.composite.description'),
        lifespan: t('types.veneerTypes.composite.lifespan'),
        idealFor: t('types.veneerTypes.composite.idealFor'),
        keyFeatures: t('types.veneerTypes.composite.keyFeatures', { returnObjects: true }) as string[]
    },
    {
        id: 'emax',
        icon: Gem,
        title: t('types.veneerTypes.emax.title'),
        tagline: t('types.veneerTypes.emax.tagline'),
        attributes: [
            t('types.veneerTypes.emax.specs.lifespan'),
            t('types.veneerTypes.emax.specs.extremeStrength'),
            t('types.veneerTypes.emax.specs.chippingResistant')
        ],
        description: t('types.veneerTypes.emax.description'),
        lifespan: t('types.veneerTypes.emax.lifespan'),
        idealFor: t('types.veneerTypes.emax.idealFor'),
        keyFeatures: t('types.veneerTypes.emax.keyFeatures', { returnObjects: true }) as string[]
    },
    {
        id: 'lumineers',
        icon: Sparkles,
        title: t('types.veneerTypes.lumineers.title'),
        tagline: t('types.veneerTypes.lumineers.tagline'),
        attributes: [
            t('types.veneerTypes.lumineers.specs.lifespan'),
            t('types.veneerTypes.lumineers.specs.noPrep'),
            t('types.veneerTypes.lumineers.specs.reversible')
        ],
        description: t('types.veneerTypes.lumineers.description'),
        lifespan: t('types.veneerTypes.lumineers.lifespan'),
        idealFor: t('types.veneerTypes.lumineers.idealFor'),
        keyFeatures: t('types.veneerTypes.lumineers.keyFeatures', { returnObjects: true }) as string[]
    },
    {
        id: 'zirconium',
        icon: Shield,
        title: t('types.veneerTypes.zirconium.title'),
        tagline: t('types.veneerTypes.zirconium.tagline'),
        attributes: [
            t('types.veneerTypes.zirconium.specs.lifespan'),
            t('types.veneerTypes.zirconium.specs.maximumStrength'),
            t('types.veneerTypes.zirconium.specs.biocompatible')
        ],
        description: t('types.veneerTypes.zirconium.description'),
        lifespan: t('types.veneerTypes.zirconium.lifespan'),
        idealFor: t('types.veneerTypes.zirconium.idealFor'),
        keyFeatures: t('types.veneerTypes.zirconium.keyFeatures', { returnObjects: true }) as string[]
    },
    {
        id: 'same-day',
        icon: Clock,
        title: t('types.veneerTypes.sameDay.title'),
        tagline: t('types.veneerTypes.sameDay.tagline'),
        attributes: [
            t('types.veneerTypes.sameDay.specs.lifespan'),
            t('types.veneerTypes.sameDay.specs.convenient'),
            t('types.veneerTypes.sameDay.specs.naturalLook')
        ],
        description: t('types.veneerTypes.sameDay.description'),
        lifespan: t('types.veneerTypes.sameDay.lifespan'),
        idealFor: t('types.veneerTypes.sameDay.idealFor'),
        keyFeatures: t('types.veneerTypes.sameDay.keyFeatures', { returnObjects: true }) as string[]
    }
];

    const [expandedCard, setExpandedCard] = useState<string | null>(null);
    const [containerHeight, setContainerHeight] = useState<number>(600);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const introRef = useRef<HTMLParagraphElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const expandedCardRef = useRef<HTMLDivElement>(null);

    // Calculate container height based on grid content
    useEffect(() => {
        if (cardsRef.current) {
            const updateHeight = () => {
                const rect = cardsRef.current?.getBoundingClientRect();
                if (rect) {
                    // Always calculate based on grid layout, not expansion state
                    const baseHeight = Math.max(600, rect.height + 20);
                    setContainerHeight(baseHeight);
                }
            };

            // Only update height when not expanded to prevent layout shifts
            if (!expandedCard) {
                updateHeight();
                window.addEventListener('resize', updateHeight);

                // Update height after grid renders
                const timer = setTimeout(updateHeight, 100);

                return () => {
                    window.removeEventListener('resize', updateHeight);
                    clearTimeout(timer);
                };
            }
        }
    }, [expandedCard]);

    // Click outside handler to close expanded card
    useEffect(() => {
        if (!expandedCard) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (expandedCardRef.current && !expandedCardRef.current.contains(event.target as Node)) {
                closeCard();
            }
        };

        // Add event listener with a small delay to prevent immediate closure
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [expandedCard]);

    // GSAP animations - tied to scroll position
    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states for all animated elements
            gsap.set([titleRef.current, introRef.current], { opacity: 0, y: 50 });
            if (cardsRef.current?.children) {
                gsap.set(cardsRef.current.children, { opacity: 0, y: 60, scale: 0.9 });
            }

            // Title animation - tied to scroll
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Introduction animation - tied to scroll
            gsap.to(introRef.current, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: introRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Individual card animations - each card appears based on its own scroll position
            if (cardsRef.current?.children) {
                Array.from(cardsRef.current.children).forEach((card, index) => {
                    gsap.to(card, {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 1,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: card,
                            start: "top 85%", // Card becomes visible when its top hits 85% of viewport
                            end: "bottom 15%",
                            toggleActions: "play none none reverse",
                            scrub: false
                        }
                    });
                });
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const toggleCard = (cardId: string) => {
        setExpandedCard(expandedCard === cardId ? null : cardId);
    };

    const closeCard = () => {
        setExpandedCard(null);
    };

    return (
        <section
            id="types"
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gold/20 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif opacity-0"
                        style={{ transform: 'translateY(50px)' }}
                    >
                        {t('types.title')}
                    </h2>
                    <p
                        ref={introRef}
                        className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed opacity-0"
                        style={{ transform: 'translateY(50px)' }}
                    >
                        {t('types.subtitle')}
                    </p>
                </div>

                {/* Cards Container — let the grid size itself naturally.
                    Only pin a min-height while a card is expanded so the
                    grid doesn't collapse behind the floating expanded card. */}
                <div
                    ref={containerRef}
                    className="relative w-full max-w-7xl mx-auto"
                    style={expandedCard ? { minHeight: `${containerHeight}px` } : undefined}
                >
                    <LayoutGroup>
                        {/* Grid View */}
                        <AnimatePresence mode="wait">
                            {!expandedCard && (
                                <motion.div
                                    key="grid-view"
                                    ref={cardsRef}
                                    initial={false} // Prevent initial animation on return
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{
                                        duration: 0.3,
                                        ease: "easeInOut"
                                    }}
                                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr w-full opacity-0"
                                    style={{ transform: 'translateY(60px) scale(0.9)' }}
                                >
                                    {veneerTypes.map((type, index) => (
                                        <motion.div
                                            key={`grid-${type.id}`}
                                            layoutId={`card-${type.id}`}
                                            whileHover={{ scale: 1.02, y: -5 }}
                                            transition={{
                                                type: "spring",
                                                stiffness: 300,
                                                damping: 30
                                            }}
                                            className="h-full cursor-pointer"
                                            onClick={() => toggleCard(type.id)}
                                        >
                                            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-gold/50 dark:hover:border-gold/50 transition-all duration-300 shadow-lg hover:shadow-2xl group overflow-hidden h-full flex flex-col">
                                                <CardContent className="p-0 flex flex-col h-full">
                                                    {/* Card Header */}
                                                    <div className="p-6 pb-4 flex-1 flex flex-col">
                                                        <div className="relative mb-4">
                                                            <div className={cn(
                                                                "w-16 h-16 bg-gold rounded-2xl flex items-center justify-center absolute top-0 group-hover:scale-110 transition-transform duration-300",
                                                                isRTL ? "right-0" : "left-0"
                                                            )}>
                                                                <type.icon className="w-8 h-8 text-white" />
                                                            </div>
                                                            <div className={cn(
                                                                "w-full",
                                                                isRTL ? "pr-20" : "pl-20"
                                                            )}>
                                                                <h3 className="text-2xl font-bold text-navy dark:text-white group-hover:text-gold dark:group-hover:text-gold transition-colors duration-300">
                                                                    {type.title}
                                                                </h3>
                                                            </div>
                                                        </div>

                                                        {/* Subtle Divider */}
                                                        <div className="w-full h-px bg-gold mb-4"></div>

                                                        {/* Tagline - Fixed Height */}
                                                        <div className="h-14 mb-4 flex items-start">
                                                            <p className="text-lg font-medium text-navy dark:text-slate-300 leading-relaxed overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                                {type.tagline}
                                                            </p>
                                                        </div>

                                                        {/* Key Attributes Pills - Always at same level */}
                                                        <div className="flex flex-wrap gap-2 mb-6">
                                                            {(Array.isArray(type.attributes) ? type.attributes : []).map((attribute, attrIndex) => (
                                                                <span
                                                                    key={attrIndex}
                                                                    className="px-3 py-1 bg-gold/10 border border-gold/30 rounded-full text-sm font-medium text-navy dark:text-gold whitespace-nowrap"
                                                                >
                                                                    {attribute}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Learn More Button */}
                                                    <div className="p-6 pt-0 mt-auto">
                                                        <Button
                                                            className="w-full bg-gold hover:bg-gold-600 text-navy font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gold hover:border-gold-600"
                                                        >
                                                            {t('types.labels.learnMore')}
                                                            <ChevronDown className={cn(
                                "w-5 h-5",
                                isRTL ? "mr-2" : "ml-2"
                            )} />
                                                        </Button>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Expanded Drawer View - Absolute positioned to not affect layout */}
                        <AnimatePresence>
                            {expandedCard && (() => {
                                const selectedType = veneerTypes.find(type => type.id === expandedCard);
                                if (!selectedType) return null;

                                return (
                                    <motion.div
                                        key={`expanded-${expandedCard}`}
                                        ref={expandedCardRef}
                                        layoutId={`card-${expandedCard}`}
                                        className="absolute inset-0 z-50"
                                        style={{ height: `${Math.min(containerHeight, 650)}px` }} // Cap maximum height
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 30,
                                            opacity: { duration: 0.2 }
                                        }}
                                    >
                                        <Card className="bg-white dark:bg-slate-800 border-gold dark:border-gold shadow-2xl w-full h-full overflow-hidden">
                                            <CardContent className="p-0 h-full flex flex-col">
                                                {/* Header - Fixed */}
                                                <div className="p-4 pb-3 border-b border-slate-200 dark:border-slate-700 relative shrink-0">
                                                    {/* Close Button */}
                                                    <motion.button
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ delay: 0.2 }}
                                                        onClick={closeCard}
                                                        className={cn(
                                                            "absolute top-4 w-8 h-8 bg-gold hover:bg-gold-600 rounded-full flex items-center justify-center text-white transition-colors z-20",
                                                            isRTL ? "left-4" : "right-4"
                                                        )}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </motion.button>

                                                    <div className="relative mb-3 pr-12">
                                                        <div className={cn(
                                                            "w-14 h-14 bg-gold rounded-2xl flex items-center justify-center absolute top-0",
                                                            isRTL ? "right-12" : "left-0"
                                                        )}>
                                                            <selectedType.icon className="w-7 h-7 text-white" />
                                                        </div>
                                                        <div className={cn(
                                                            "w-full",
                                                            isRTL ? "pr-20" : "pl-18"
                                                        )}>
                                                            <h3 className="text-2xl font-bold text-navy dark:text-white mb-1">
                                                                {selectedType.title}
                                                            </h3>
                                                            <p className="text-base text-gold dark:text-gold font-medium">
                                                                {selectedType.tagline}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Attributes */}
                                                    <div className="flex flex-wrap gap-2">
                                                        {(Array.isArray(selectedType.attributes) ? selectedType.attributes : []).map((attribute, attrIndex) => (
                                                            <span
                                                                key={attrIndex}
                                                                className="px-2 py-1 bg-gold/10 border border-gold/30 rounded-full text-xs font-medium text-navy dark:text-gold"
                                                            >
                                                                {attribute}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Content - Scrollable */}
                                                <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gold scrollbar-track-transparent">
                                                    <div className="p-4 space-y-4">
                                                        {/* Description */}
                                                        <motion.div
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ delay: 0.3 }}
                                                        >
                                                            <h4 className="text-lg font-bold text-navy dark:text-white mb-2">{t('types.labels.description')}</h4>
                                                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                                                {selectedType.description}
                                                            </p>
                                                        </motion.div>

                                                        {/* Details Grid */}
                                                        <div className="grid md:grid-cols-2 gap-4">
                                                            {/* Lifespan */}
                                                            <motion.div
                                                                initial={{ opacity: 0, y: 20 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                transition={{ delay: 0.4 }}
                                                                className="bg-gold/5 rounded-xl p-3 border border-gold/20"
                                                            >
                                                                <h4 className="text-base font-bold text-navy dark:text-white mb-2">{t('types.labels.expectedLifespan')}</h4>
                                                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-xs">
                                                                    {selectedType.lifespan}
                                                                </p>
                                                            </motion.div>

                                                            {/* Ideal For */}
                                                            <motion.div
                                                                initial={{ opacity: 0, y: 20 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                transition={{ delay: 0.5 }}
                                                                className="bg-gold/5 rounded-xl p-3 border border-gold/20"
                                                            >
                                                                <h4 className="text-base font-bold text-navy dark:text-white mb-2">{t('types.labels.idealFor')}</h4>
                                                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-xs">
                                                                    {selectedType.idealFor}
                                                                </p>
                                                            </motion.div>
                                                        </div>

                                                        {/* Key Features */}
                                                        <motion.div
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ delay: 0.6 }}
                                                            className="pb-2"
                                                        >
                                                            <h4 className="text-lg font-bold text-navy dark:text-white mb-3">{t('types.labels.keyFeatures')}</h4>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                {(Array.isArray(selectedType.keyFeatures) ? selectedType.keyFeatures : []).map((feature, featureIndex) => (
                                                                    <motion.div
                                                                        key={featureIndex}
                                                                        initial={{ opacity: 0, x: -20 }}
                                                                        animate={{ opacity: 1, x: 0 }}
                                                                        transition={{ delay: 0.7 + featureIndex * 0.1 }}
                                                                        className={cn(
                                                                            "relative bg-white dark:bg-navy-950 rounded-lg p-2 border border-slate-200 dark:border-slate-700",
                                                                            isRTL ? "pr-6" : "pl-6"
                                                                        )}
                                                                    >
                                                                        <div className={cn(
                                                                            "w-1.5 h-1.5 bg-gold rounded-full absolute mt-1.5",
                                                                            isRTL ? "right-2" : "left-2"
                                                                        )}></div>
                                                                        <span className="text-slate-600 dark:text-slate-300 leading-relaxed text-xs block">
                                                                            {feature}
                                                                        </span>
                                                                    </motion.div>
                                                                ))}
                                                            </div>
                                                        </motion.div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })()}
                        </AnimatePresence>
                    </LayoutGroup>
                </div>
            </div>
        </section>
    );
} 
